import { useCallback, useState } from "react";

export function useDrawerController<T extends object>() {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<T | undefined>(undefined);
  const [mode, setMode] = useState<"add" | "edit" | "clone" | "view" | undefined>(undefined);

  // Open drawer helper with optional data
  const openDrawer = useCallback((data?: T) => {
    if (data !== undefined) {
      setEditingItem(data);
    } else {
      setEditingItem(undefined);
    }
    setDrawerVisible(true);
  }, []);

  // Close drawer and reset all states
  const closeDrawer = useCallback(() => {
    setDrawerVisible(false);
    setEditingItem(undefined);
    setMode(undefined);
  }, []);

  // Reset editing state but keep drawer open if needed
  const reset = useCallback(() => {
    setEditingItem(undefined);
    setMode(undefined);
  }, []);

  // Mode-specific openers
  const openAdd = useCallback(() => {
    setMode("add");
    setEditingItem(undefined);
    setDrawerVisible(true);
  }, []);

  const openEdit = useCallback(
    (data: T) => {
      setMode("edit");
      openDrawer(data);
    },
    [openDrawer],
  );

  const openClone = useCallback(
    (data: T) => {
      setMode("clone");
      openDrawer(data);
      if (!("id" in data)) {
        console.error("Need contain 'id'", data);
        return;
      }
      const { id, ...restData } = data;
      console.log("clone item id", id);
      openDrawer(restData as T);
    },
    [openDrawer],
  );

  const openView = useCallback(
    (data: T) => {
      setMode("view");
      openDrawer(data);
    },
    [openDrawer],
  );

  // Mode flags for easy conditional rendering
  const isAdd = mode === "add";
  const isEdit = mode === "edit";
  const isClone = mode === "clone";
  const isView = mode === "view";

  return {
    drawerVisible,
    editingItem,
    mode,
    isAdd,
    isEdit,
    isClone,
    isView,
    openDrawer,
    closeDrawer,
    reset,
    openAdd,
    openEdit,
    openClone,
    openView,
  };
}
